import React, { PureComponent, Fragment } from "react";
import {
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Bar,
  ComposedChart,
} from "recharts";
import { MAIN_COLORS } from "components/Utils/Utils.js";
import { XRayTooltip } from "components/Utils/XrayUtils.js";
import { getXrayTooltipPosition, findParentNodeByClass } from "helpers/DOM";

import Uri from "jsuri";
import {
  selectFormat,
  CustomizedYAxisTick,
  CustomizedYAxisTickPercentage,
  Error,
  customColorIndex,
  isDateFromDataInISOFormat,
} from "components/Utils/Utils.js";

class MixAccuracy extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      widgetsData: props.data?.data || [],
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
    };

    this.node = React.createRef();
    this.divPosition = React.createRef();
    this.updatePosition = this.updatePosition.bind(this);
  }

  updatePosition() {
    if (!this.chartRef) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef(this);
    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  colorsIndex = () => new Uri(this.props.url).getQueryParamValue("colors");

  handleClickOutside = (e) => {
    if (
      !e.target.classList.contains("recharts-line") &&
      !findParentNodeByClass(e.target, "xray-tooltip-container")
    ) {
      this.setState({ tooltip: { show: false } });
    }
  };

  legendElement = (data) =>
    data.map((entry, i) => (
      <span key={i} className="mt-2 legend mt-md-0">
        <span
          className="legend__icon"
          style={{
            background: MAIN_COLORS[customColorIndex(i, this.colorsIndex())],
          }}
        ></span>
        <span
          className="legend__text"
          style={{
            color: MAIN_COLORS[customColorIndex(i, this.colorsIndex())],
          }}
        >
          {entry}
        </span>
      </span>
    ));

  renderLegends = (data) => {
    if (!data || !data.length) return null;
    const targets = [
      ...new Set(
        data[0].list.map((item) => `${item.target} (${item.accuracy_type})`)
      ),
    ];
    return this.legendElement(targets);
  };

  handleLineClick = (data) => {
    if (this.state.tooltip.show && data.date === this.state.tooltip.date) {
      this.setState({ tooltip: { show: false } });
    } else {
      this.setState({
        tooltip: {
          ...data,
          show: true,
          width: 200,
        },
      });
    }
  };

  getExportData = () => {
    const { data } = this.props.data;

    // Transform the data into a flat array of objects for CSV export
    const exportData = data.flatMap((datePoint) =>
      datePoint.list.map((item) => ({
        date: datePoint.date,
        scenario: item.scenario,
        target: item.target,
        accuracy_type: item.accuracy_type,
        target_value: item.target_value,
        accuracy: (item.accuracy * 100).toFixed(2) + "%",
      }))
    );

    return {
      data: exportData,
      opts: {
        type: "other",
        date: {
          defined: isDateFromDataInISOFormat(data),
          format: "YYYY-MM-DDThh:mm:ssZ",
          viewFormat: "YYYY-MM-DD",
        },
      },
    };
  };

  render() {
    const { data } = this.props;

    if (!data?.data || !Array.isArray(data.data) || data.data.length === 0) {
      return <Error minHeight={360} />;
    }

    // Get unique combinations of target and accuracy_type
    const lineKeys = [
      ...new Set(
        data.data[0].list.map((item) => `${item.target}_${item.accuracy_type}`)
      ),
    ];

    return (
      <Fragment>
        <div ref={this.divPosition}>
          {this.renderLegends(data.data)}
          <div className="mt-2 d-flex">
            <ResponsiveContainer height={370} width="100%">
              <ComposedChart
                data={data.data}
                margin={{ top: 8, right: 30, bottom: 15, left: -38 }}
                ref={(ref) => (this.chartRef = ref)}
              >
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  scale="auto"
                />
                <YAxis
                  yAxisId="left"
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value) => `${(value * 100).toFixed(1)}%`}
                  tick={<CustomizedYAxisTickPercentage />}
                  domain={[
                    (dataMin) => dataMin - 0.05,
                    (dataMax) => dataMax + 0.05,
                  ]}
                  allowDataOverflow={true}
                  scale="linear"
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value) => selectFormat("number", value)}
                  tick={<CustomizedYAxisTick />}
                />
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const dataPoint = data.data.find((d) => d.date === label);
                      return (
                        <div className="standard-tooltip">
                          <header className="standard-tooltip__header">
                            <span className="standard-tooltip__key-header">
                              Date:
                            </span>
                            <span className="standard-tooltip__value-header">
                              {label}
                            </span>
                          </header>
                          <main className="standard-tooltip__main">
                            {[...dataPoint.list]
                              .sort((a, b) => b.accuracy - a.accuracy)
                              .map((entry) => {
                                const key = `${entry.target}_${entry.accuracy_type}`;
                                const colorIndex = lineKeys.indexOf(key);
                                return (
                                  <div
                                    key={`${entry.target}-${entry.accuracy_type}`}
                                  >
                                    <div className="standard-tooltip__item">
                                      <span
                                        className="standard-tooltip__icon"
                                        style={{
                                          background:
                                            MAIN_COLORS[
                                              customColorIndex(
                                                colorIndex,
                                                this.colorsIndex()
                                              )
                                            ],
                                        }}
                                      ></span>
                                      <span
                                        className="standard-tooltip__key"
                                        style={{
                                          color:
                                            MAIN_COLORS[
                                              customColorIndex(
                                                colorIndex,
                                                this.colorsIndex()
                                              )
                                            ],
                                        }}
                                      >
                                        {entry.target} ({entry.accuracy_type})
                                      </span>
                                      <span className="standard-tooltip__value">
                                        {(entry.accuracy * 100).toFixed(1)}%
                                      </span>
                                    </div>
                                    <div className="standard-tooltip__details">
                                      <div className="standard-tooltip__detail-item">
                                        <span className="standard-tooltip__detail-label">
                                          Scenario:
                                        </span>
                                        <span className="standard-tooltip__detail-value">
                                          {entry.scenario}
                                        </span>
                                      </div>
                                      <div className="standard-tooltip__detail-item">
                                        <span className="standard-tooltip__detail-label">
                                          Target Value:
                                        </span>
                                        <span className="standard-tooltip__detail-value">
                                          {selectFormat(
                                            "number",
                                            entry.target_value
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                          </main>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                {lineKeys.map((key, index) => {
                  const [target, accuracyType] = key.split("_");
                  return (
                    <Line
                      key={key}
                      yAxisId="left"
                      type="monotone"
                      dataKey={(data) => {
                        const entry = data.list.find(
                          (item) =>
                            item.target === target &&
                            item.accuracy_type === accuracyType
                        );
                        return entry ? entry.accuracy : null;
                      }}
                      stroke={
                        MAIN_COLORS[customColorIndex(index, this.colorsIndex())]
                      }
                      strokeWidth={2}
                      dot={false}
                      name={`${target} (${accuracyType})`}
                      onClick={this.handleLineClick}
                    />
                  );
                })}
                {lineKeys.map((key, index) => {
                  const [target, accuracyType] = key.split("_");
                  return (
                    <Bar
                      key={`${key}_bar`}
                      yAxisId="right"
                      dataKey={(data) => {
                        const entry = data.list.find(
                          (item) =>
                            item.target === target &&
                            item.accuracy_type === accuracyType
                        );
                        return entry ? entry.target_value : null;
                      }}
                      fill={
                        MAIN_COLORS[customColorIndex(index, this.colorsIndex())]
                      }
                      fillOpacity={0.3}
                      name={`${target} (${accuracyType}) Value`}
                    />
                  );
                })}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div
            className="xray-tooltip-container"
            style={{
              top: "5.4rem",
              left:
                this.state.tooltip.x + this.state.tooltip.width + 220 <
                this.state.width
                  ? this.state.tooltip.x + this.state.tooltip.width + 15 + "px"
                  : this.state.tooltip.x -
                    getXrayTooltipPosition(
                      this.divPosition.current,
                      this.state.tooltip
                    ) +
                    "px",
              opacity: this.state.tooltip.show ? 1.0 : 0.0,
              visibility: this.state.tooltip.show ? "visible" : "hidden",
            }}
          >
            <XRayTooltip
              data={this.state.tooltip}
              close={() => this.setState({ tooltip: { show: false } })}
            />
          </div>
        </div>
      </Fragment>
    );
  }
}

export default MixAccuracy;
